import type { PublicPinnedTrophy, TrophyTier } from '@crowdfpl/domain/gamification';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { type ReactElement } from 'react';
import {
  formatRarityPercent,
  TROPHY_ACCENT,
  TROPHY_EMOJI,
  TROPHY_TAGLINE_COPY,
} from '@/components/gamification/trophy-copy';
import { cn } from '@/helpers/utils';
import { useGamificationFeatureEnabled } from '@/hooks/data/config';

type PinnedTrophyBadgeProps = {
  pinnedTrophy: PublicPinnedTrophy | null | undefined;
  className?: string;
  /** Decorative inside a parent link/button — avoids nested interactive controls. */
  decorative?: boolean;
};

function formatIdentityLabel(value: string): string {
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTierLabel(tier: TrophyTier | null): string | null {
  if (!tier) {
    return null;
  }
  return formatIdentityLabel(tier);
}

/**
 * Compact emoji medallion for a public pinned trophy (Lovable parity).
 * Returns null when there is no pin.
 */
export function PinnedTrophyBadge({
  pinnedTrophy,
  className,
  decorative = false,
}: PinnedTrophyBadgeProps): ReactElement | null {
  const gamificationEnabled = useGamificationFeatureEnabled();
  if (!gamificationEnabled || !pinnedTrophy) {
    return null;
  }

  const trophyName = formatIdentityLabel(pinnedTrophy.trophy_id);
  const tierLabel = formatTierLabel(pinnedTrophy.highest_tier);
  const rarityLabel =
    pinnedTrophy.rarity_pct == null ? null : `Rarity ${formatRarityPercent(pinnedTrophy.rarity_pct)}%`;
  const accessibleName = [trophyName, tierLabel, rarityLabel].filter(Boolean).join(', ');
  const tagline = TROPHY_TAGLINE_COPY[pinnedTrophy.trophy_id];
  const accent = TROPHY_ACCENT[pinnedTrophy.trophy_id];
  const emoji = TROPHY_EMOJI[pinnedTrophy.trophy_id];

  const badge = (
    <span
      data-testid="pinned-trophy-badge"
      data-trophy-id={pinnedTrophy.trophy_id}
      className={cn(
        'inline-flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-[5px] text-[10px] leading-none',
        className,
      )}
      style={{
        background: `color-mix(in srgb, ${accent} 13%, transparent)`,
        boxShadow: `0 0 0 1px color-mix(in srgb, ${accent} 33%, transparent) inset`,
      }}
      aria-label={decorative ? undefined : accessibleName}
      aria-hidden={decorative || undefined}
      title={decorative ? accessibleName : undefined}
    >
      {emoji}
    </span>
  );

  if (decorative) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-semibold">{accessibleName}</div>
          {tagline ? <div className="text-[11px] text-muted-foreground">{tagline}</div> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
